import { COMMA, ENTER, SPACE } from '@angular/cdk/keycodes';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatChipInputEvent, MatChipList } from '@angular/material/chips';
import structuredClone from '@ungap/structured-clone';
import { Subscription } from 'rxjs';
import { ReplyToEmailModeEnum, SendByEmailSettings } from 'src/app/shared/models/send-by-mail';
import { BasicService } from 'src/app/shared/services/basic.service';
import { SendConversationByEmailService } from 'src/app/shared/services/send-by-email.service';

import { ChipListCustomErrorEnum } from './models';

@Component({
  selector: 'app-send-by-email-settings',
  templateUrl: './send-by-email-settings.component.html',
  styleUrls: ['./send-by-email-settings.component.scss'],
})
export class SendByEmailSettingsComponent implements OnInit, OnDestroy {
  @ViewChild('chipListMandatoryRecipients') chipListMandatoryRecipients: MatChipList;
  @ViewChild('chipListauthorizedDomains') chipListauthorizedDomains: MatChipList;
  separatorKeysCodes: number[] = [ENTER, COMMA, SPACE];

  private initialSettings: SendByEmailSettings;
  private subscriptions = new Subscription();

  private regexDomain = /^@([\w-]+\.)+[\w-]{2,4}$/;

  chipListCustomErrorEnum = ChipListCustomErrorEnum;
  replyToEmailModeEnum = Object.keys(ReplyToEmailModeEnum);
  recipientError: ChipListCustomErrorEnum = null;
  domainError: ChipListCustomErrorEnum = null;
  displayReplyToEmail: boolean;

  form = this.formBuilder.group({
    enabled: [false],
    senderEmail: ['no-reply@alcmeon.com', [Validators.required, Validators.email]],
    senderName: ['Alcméon', [Validators.required]],
    mandatoryRecipient: ['', Validators.email],
    mandatoryRecipients: [[]],
    authorizedDomain: ['', [Validators.pattern(this.regexDomain)]],
    authorizedDomains: [[]],
    replyToEmailMode: ['', [Validators.required]],
    replyToOtherEmail: ['', [Validators.email, Validators.required]],
    defaultObject: [''],
    defaultComment: [''],
  });

  constructor(
    private basicService: BasicService,
    private formBuilder: FormBuilder,
    private sendConversationByEmailService: SendConversationByEmailService
  ) {}

  ngOnInit(): void {
    this.sendConversationByEmailService.getSettings().subscribe({
      next: (settings: SendByEmailSettings[]) => {
        if (settings.length) {
          // structuredClone exists in JS API, but need a polyfill with node < 17
          this.initialSettings = structuredClone(settings[0]);
          this.patchForm(settings[0]);
        }

        this.computeDisplayReplyToEmail(this.form.value.replyToEmailMode);
      },
    });

    this.subscriptions.add(
      this.form.get('mandatoryRecipient').valueChanges.subscribe(() => {
        this.chipListMandatoryRecipients.errorState = false;
      })
    );

    this.subscriptions.add(
      this.form.get('authorizedDomain').valueChanges.subscribe(() => {
        this.chipListauthorizedDomains.errorState = false;
      })
    );

    this.subscriptions.add(
      this.form.get('replyToEmailMode').valueChanges.subscribe(value => {
        this.computeDisplayReplyToEmail(value);
      })
    );
  }

  computeDisplayReplyToEmail(value: ReplyToEmailModeEnum) {
    if (value === ReplyToEmailModeEnum.other) {
      this.form.get('replyToOtherEmail').enable();
      this.displayReplyToEmail = true;
    } else {
      this.form.get('replyToOtherEmail').disable();
      this.displayReplyToEmail = false;
    }
  }

  patchForm(settings: SendByEmailSettings) {
    this.form.patchValue(settings);
  }

  /**
   * Adds a recipient from the input in the chip list
   * Performs all validity controls
   */
  addRecipient(event: MatChipInputEvent) {
    if (!event.value) {
      return;
    }

    // if email invalid
    if (this.form.get('mandatoryRecipient').hasError('email')) {
      this.chipListMandatoryRecipients.errorState = true;
      this.recipientError = ChipListCustomErrorEnum.invalid;
      return;
    }

    // if email already in list
    if (this.form.get('mandatoryRecipients').value.find(elt => elt === event.value)) {
      this.chipListMandatoryRecipients.errorState = true;
      this.recipientError = ChipListCustomErrorEnum.exists;
      event.chipInput.clear();

      // non blocking error, so we remove the message after a delay
      setTimeout(() => {
        this.chipListMandatoryRecipients.errorState = false;
      }, 3000);
      return;
    }

    // finaly if no error
    this.form.get('mandatoryRecipients').value.push(event.value);
    this.form.get('mandatoryRecipients').updateValueAndValidity();
    event.chipInput.clear();
  }

  removeMandatoryRecipient(recipient: string): void {
    const index = this.form.get('mandatoryRecipients').value.indexOf(recipient);
    if (index >= 0) {
      this.form.get('mandatoryRecipients').value.splice(index, 1);
      this.form.get('mandatoryRecipients').updateValueAndValidity();
      this.form.markAsDirty();
    }
  }

  /**
   * Adds a recipient from the input in the chip list
   * Performs all validity controls
   */
  addDomain(event: MatChipInputEvent) {
    if (!event.value) {
      return;
    }

    // if domain invalid
    if (this.form.get('authorizedDomain').hasError('pattern')) {
      this.chipListauthorizedDomains.errorState = true;
      this.domainError = ChipListCustomErrorEnum.invalid;
      return;
    }

    // if domain already in list
    if (this.form.get('authorizedDomains').value.find(elt => elt === event.value)) {
      this.chipListauthorizedDomains.errorState = true;
      this.domainError = ChipListCustomErrorEnum.exists;
      event.chipInput.clear();

      // non blocking error, so we remove the message after a delay
      setTimeout(() => {
        this.chipListauthorizedDomains.errorState = false;
      }, 3000);
      return;
    }

    // finaly if no error
    this.form.get('authorizedDomains').value.push(event.value);
    this.form.get('authorizedDomains').updateValueAndValidity();
    event.chipInput.clear();
  }

  removeDomain(domain: string): void {
    const index = this.form.get('authorizedDomains').value.indexOf(domain);
    if (index >= 0) {
      this.form.get('authorizedDomains').value.splice(index, 1);
      this.form.get('authorizedDomains').updateValueAndValidity();
      this.form.markAsDirty();
    }
  }

  save() {
    const {
      authorizedDomains,
      defaultComment,
      defaultObject,
      enabled,
      mandatoryRecipients,
      senderEmail,
      senderName,
      replyToEmailMode,
      replyToOtherEmail,
    } = this.form.value;

    const data: SendByEmailSettings = {
      authorizedDomains,
      defaultComment,
      defaultObject,
      enabled,
      mandatoryRecipients,
      senderEmail,
      senderName,
      replyToEmailMode,
      replyToOtherEmail,
    };

    this.sendConversationByEmailService.saveSettings(data).subscribe({
      next: () => {
        this.form.markAsPristine();
        this.basicService.toastSuccess('save-ok');
      },
    });
  }

  reset() {
    this.patchForm(this.initialSettings);
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
